import { type Node, type Edge } from "@xyflow/react";
import { type JsonPropertyNodeData, type MappedValueToken } from "./components/JsonPropertyNode";
import { ElementType, IElement, IElementDictionary } from "processEditor/model/IProcessData";
import { extractSourcePaths, extractEnvPaths, removeSourcePath, hasBareReference, hasHandlebarsReference, directTextMappingToCsharp, collectSourceRefs, formatSourceToken, resolveEditorMode, type VdmExpressionMode } from "./vdmMappingExpressions";

const ROW_HEIGHT = 30;
const CONTENT_PADDING = 16;
const PANEL_GAP = 58;

export interface VdmMappingEntry {
    mode: "text" | "csharp";
    value: string;
    /** Source property type when this mapping was last saved. Used for schema-change detection. */
    sourceType?: string;
    /** Target property type when this mapping was last saved. Used for schema-change detection. */
    targetType?: string;
}

export interface JsonSchemaProperty {
    key: string;
    type: string;
    pathSegment?: string;
    displayType?: string;
    collapsedDisplayType?: string;
    children?: JsonSchemaProperty[];
    isCollapsed?: boolean;
}

export function collectSchemaPaths(schema: JsonSchemaProperty[], parentPath = ""): Set<string> {
    const paths = new Set<string>();
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        paths.add(path);
        if (prop.children) {
            for (const childPath of collectSchemaPaths(prop.children, path)) {
                paths.add(childPath);
            }
        }
    }
    return paths;
}

/** Collects schema paths with their types (e.g. "foo.bar:string") so type changes are detectable */
export function collectSchemaPathsWithTypes(schema: JsonSchemaProperty[], parentPath = ""): Set<string> {
    const paths = new Set<string>();
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        paths.add(`${path}:${prop.type}`);
        if (prop.children) {
            for (const childPath of collectSchemaPathsWithTypes(prop.children, path)) {
                paths.add(childPath);
            }
        }
    }
    return paths;
}

export function findSchemaType(schema: JsonSchemaProperty[], targetPath: string): string | undefined {
    return findSchemaTypeRecursive(schema, normalizePathForSchema(targetPath), "");
}

export function collectLeafSchemaPaths(schema: JsonSchemaProperty[], parentPath = ""): Set<string> {
    const paths = new Set<string>();
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        if (prop.type !== "object") {
            paths.add(path);
        }
        if (prop.children) {
            for (const childPath of collectLeafSchemaPaths(prop.children, path)) {
                paths.add(childPath);
            }
        }
    }
    return paths;
}

/**
 * 1. Drops entries whose target path no longer exists in the target schema.
 * 2. Removes source-path references that no longer exist in the source schema.
 * Returns the cleaned mappings, or `null` if nothing changed.
 */
export function cleanStaleMappings(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> | null {
    const validSourcePaths = collectSchemaPaths(sourceSchema);
    const validTargetPaths = collectSchemaPaths(targetSchema);
    let hasStale = false;
    const cleaned: Record<string, VdmMappingEntry> = {};

    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        if (!validTargetPaths.has(targetPath)) {
            hasStale = true;
            continue;
        }

        const sourcePaths = extractSourcePaths(entry.value, entry.mode);
        if (sourcePaths.length === 0) {
            cleaned[targetPath] = entry;
            continue;
        }

        const stalePaths = sourcePaths.filter(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p)));
        if (stalePaths.length === 0) {
            cleaned[targetPath] = entry;
            continue;
        }

        hasStale = true;
        let newExpression = entry.value;
        for (const stalePath of stalePaths) {
            newExpression = removeSourcePath(newExpression, stalePath, entry.mode);
        }
        if (newExpression.trim()) {
            cleaned[targetPath] = { ...entry, value: newExpression };
        }
    }

    return hasStale ? cleaned : null;
}

/**
 * Forces csharp mode for any text-mode mapping whose target is now a non-string type, since text mode
 * emits a handlebars string that fails strict validation against a non-string target. A pure direct
 * mapping is converted to its csharp form; composite expressions keep their value and are flagged as
 * broken for the user to fix. Returns the updated mappings, or null if nothing changed.
 */
export function normalizeMappingModes(
    mappingExpressions: Record<string, VdmMappingEntry>,
    targetSchema: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> | null {
    let changed = false;
    const updated: Record<string, VdmMappingEntry> = {};
    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        const targetType = findSchemaType(targetSchema, targetPath);
        const correctedMode = targetType ? resolveEditorMode(entry.mode, targetType) : entry.mode;
        if (correctedMode === entry.mode) {
            updated[targetPath] = entry;
            continue;
        }
        const convertedValue = correctedMode === "csharp" ? directTextMappingToCsharp(entry.value) : null;
        updated[targetPath] = { ...entry, mode: correctedMode, value: convertedValue ?? entry.value };
        changed = true;
    }
    return changed ? updated : null;
}

/**
 * When a user clicks an edge to delete it, the edge might represent a collapsed node
 * so check through the mapping expressions for any references to the source path and remove them.
 */
export function removeEdgeMappings(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourcePath: string,
    targetPath: string,
    sourceSchema?: JsonSchemaProperty[],
    targetSchema?: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> | null {
    const updated = { ...mappingExpressions };
    let changed = false;
    const targetSegs = resolvePathSegments(targetPath, targetSchema);
    const sourceSegs = resolvePathSegments(sourcePath, sourceSchema);

    for (const [tPath, entry] of Object.entries(updated)) {
        if (!isSegmentPrefix(targetSegs, resolvePathSegments(tPath, targetSchema))) {
            continue;
        }

        const refs = extractSourcePaths(entry.value, entry.mode);
        const matching = refs.filter(
            (sp) => isSegmentPrefix(sourceSegs, resolvePathSegments(sp, sourceSchema)),
        );

        if (matching.length === 0) {
            continue;
        }

        let newExpression = entry.value;
        for (const sp of matching) {
            newExpression = removeSourcePath(newExpression, sp, entry.mode);
        }

        if (newExpression.trim()) {
            updated[tPath] = { ...entry, value: newExpression };
        } else {
            delete updated[tPath];
        }
        changed = true;
    }

    return changed ? updated : null;
}

export function expressionToTokens(expression: string, validSourcePaths?: Set<string>, mode: VdmExpressionMode = "text"): MappedValueToken[] {
    const sourcePaths = extractSourcePaths(expression, mode);

    if (sourcePaths.length === 0) {
        const isInvalid = (mode === "text" && hasBareReference(expression))
            || (mode === "csharp" && hasHandlebarsReference(expression));
        return [{ text: expression, variant: isInvalid ? "invalid" : "static" }];
    }

    const hasInvalidPath = (mode === "text" && hasBareReference(expression))
        || (mode === "csharp" && hasHandlebarsReference(expression))
        || (validSourcePaths != null && sourcePaths.some(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p))));

    // Direct mapping: expression is exactly one source ref
    if (mode === "csharp") {
        const refs = collectSourceRefs(expression, mode);
        const isDirect = refs.length === 1 && refs[0].start === 0 && refs[0].tokenEnd === expression.length;
        if (isDirect) {
            return [{ text: `...${lastPathSegment(sourcePaths[0])}`, variant: hasInvalidPath ? "invalid" : "direct" }];
        }
    } else {
        const singleSourceToken = sourcePaths.length === 1 ? formatSourceToken(sourcePaths[0], mode) : null;
        if (singleSourceToken && expression === singleSourceToken) {
            return [{ text: `{{...${lastPathSegment(sourcePaths[0])}}}`, variant: hasInvalidPath ? "invalid" : "direct" }];
        }
    }

    // Modified: replace #source.path with ...lastSegment, keep everything else as-is
    const display = replaceSourceRefs(expression, mode);
    return [{ text: display, variant: hasInvalidPath ? "invalid" : "modified" }];
}

function replaceSourceRefs(expression: string, mode: VdmExpressionMode): string {
    const refs = collectSourceRefs(expression, mode);
    let result = expression;
    for (const ref of [...refs].sort((a, b) => b.start - a.start)) {
        result = result.substring(0, ref.start) + `...${lastPathSegment(ref.path)}` + result.substring(ref.tokenEnd);
    }
    return result;
}

function lastPathSegment(path: string): string {
    return path ? (path.split(".").pop() ?? path) : "#source";
}

export function expressionHasInvalidSourcePaths(expression: string, validSourcePaths: Set<string>, mode: VdmExpressionMode = "text"): boolean {
    const sourcePaths = extractSourcePaths(expression, mode);
    return sourcePaths.some(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p)))
        || (mode === "text" && hasBareReference(expression))
        || (mode === "csharp" && hasHandlebarsReference(expression));
}

function flattenProperty(
    prop: JsonSchemaProperty,
    side: "source" | "target",
    connectedIds: Set<string>,
    depth: number,
    parentPath: string,
    rowCounter: { value: number },
    parentNodeId: string,
    panelWidth: number,
    mappingExpressions?: Record<string, VdmMappingEntry>,
    targetTokenResolver?: (targetPath: string, expression: string) => MappedValueToken[],
    blockedTargetPaths?: Set<string>,
): Node[] {
    const nodes: Node[] = [];
    const path = buildSchemaPath(parentPath, prop);
    const childParentPath = path;
    const nodeId = `${side}-${path}`;
    const resolveTokens = targetTokenResolver ?? ((_p: string, expr: string) => expressionToTokens(expr));

    const isExpandable = prop.type === "object" || (prop.type === "array" && !!prop.children?.length);
    // Show handles on array nodes so the whole array can be mapped while expanded.
    const hasHandle = !isExpandable || !!prop.isCollapsed || prop.type === "array";
    const baseIsConnectable = hasHandle && prop.type !== "object";
    const isBlocked = side === "target" && !!blockedTargetPaths?.has(path);
    const isConnectable = baseIsConnectable && !isBlocked;

    const nodeWidth = panelWidth - CONTENT_PADDING * 2;
    const openBracket = isExpandable
        ? (prop.type === "array" ? "[" : "{")
        : undefined;
    const closeBracket = isExpandable
        ? (prop.type === "array" ? "]" : "}")
        : undefined;

    const mappingEntry = side === "target" && mappingExpressions ? mappingExpressions[path] : undefined;

    const nodeData: JsonPropertyNodeData = {
        propertyKey: prop.key,
        valueType: prop.isCollapsed && prop.collapsedDisplayType
            ? prop.collapsedDisplayType
            : prop.displayType ?? (isExpandable ? "" : prop.type),
        depth,
        side,
        isExpandable,
        isExpanded: !prop.isCollapsed,
        hasHandle,
        isConnected: connectedIds.has(nodeId),
        isConnectable,
        nodePath: path,
        collapsedPreview: prop.isCollapsed
            ? (!prop.displayType && !prop.collapsedDisplayType ? (prop.type === "array" ? "[]" : "{}") : undefined)
            : undefined,
        openBracket: (!prop.isCollapsed && isExpandable) ? openBracket : undefined,
        mappedTokens: mappingEntry
            ? resolveTokens(path, mappingEntry.value)
            : undefined,
        mappingExpression: mappingEntry?.value,
        targetPath: side === "target" ? path : undefined,
        targetType: side === "target" ? prop.type : undefined,
        isBlocked: side === "target" ? isBlocked : undefined,
        baseIsConnectable: side === "target" ? baseIsConnectable : undefined,
    };

    nodes.push({
        id: nodeId,
        type: "jsonProperty",
        position: { x: CONTENT_PADDING, y: CONTENT_PADDING + rowCounter.value * ROW_HEIGHT },
        data: nodeData,
        parentId: parentNodeId,
        extent: "parent" as const,
        draggable: false,
        style: { width: nodeWidth },
    });

    rowCounter.value++;

    if (prop.children && !prop.isCollapsed) {
        for (const child of prop.children) {
            nodes.push(...flattenProperty(child, side, connectedIds, depth + 1, childParentPath, rowCounter, parentNodeId, panelWidth, mappingExpressions, targetTokenResolver, blockedTargetPaths));
        }

        // Closing bracket row
        if (closeBracket) {
            nodes.push({
                id: `${nodeId}-close`,
                type: "jsonProperty",
                position: { x: CONTENT_PADDING, y: CONTENT_PADDING + rowCounter.value * ROW_HEIGHT },
                data: {
                    propertyKey: closeBracket,
                    valueType: "",
                    depth,
                    side,
                    isExpandable: false,
                    isExpanded: false,
                    hasHandle: false,
                    isConnected: false,
                    isConnectable: false,
                    isBracket: true,
                } satisfies JsonPropertyNodeData,
                parentId: parentNodeId,
                extent: "parent" as const,
                draggable: false,
                style: { width: nodeWidth },
            });
            rowCounter.value++;
        }
    }

    return nodes;
}

export function generateInitialNodes(
    containerWidth: number,
    sourceSchema?: JsonSchemaProperty[],
    targetSchema?: JsonSchemaProperty[],
    connectedEdges?: Edge[],
    mappingExpressions?: Record<string, VdmMappingEntry>,
    targetTokenResolver?: (targetPath: string, expression: string) => MappedValueToken[],
): { nodes: Node[]; maxHeight: number } {
    const DEFAULT_PANEL_HEIGHT = 200;
    const panelWidth = (containerWidth - PANEL_GAP) / 2;

    const connectedSourceIds = new Set((connectedEdges ?? []).map((e) => e.source));
    const connectedTargetIds = new Set((connectedEdges ?? []).map((e) => e.target));

    // Mark targets with source references as connected (static-value-only targets should remain unconnected).
    if (mappingExpressions) {
        for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
            if (extractSourcePaths(entry.value, entry.mode).length > 0) {
                connectedTargetIds.add(`target-${targetPath}`);
            }
        }
    }

    // Flatten property nodes and compute panel heights
    const sourceNodes: Node[] = [];
    let sourcePanelHeight = 0;
    let sourceData: Record<string, unknown> = { emptyMessage: "No source data selected." };

    if (sourceSchema && sourceSchema.length > 0) {
        const sourceRowCounter = { value: 0 };
        for (const prop of sourceSchema) {
            sourceNodes.push(...flattenProperty(prop, "source", connectedSourceIds, 0, "", sourceRowCounter, "source-panel", panelWidth));
        }
        sourcePanelHeight = CONTENT_PADDING * 2 + sourceRowCounter.value * ROW_HEIGHT;
        sourceData = {};
    }

    const targetNodes: Node[] = [];
    let targetPanelHeight = 0;
    let targetData: Record<string, unknown> = { emptyMessage: "No target schema defined." };

    if (targetSchema && targetSchema.length > 0) {
        const targetRowCounter = { value: 0 };
        const blockedTargetPaths = findBlockedTargetPaths(targetSchema, mappingExpressions);
        for (const prop of targetSchema) {
            targetNodes.push(...flattenProperty(prop, "target", connectedTargetIds, 0, "", targetRowCounter, "target-panel", panelWidth, mappingExpressions, targetTokenResolver, blockedTargetPaths));
        }
        targetPanelHeight = CONTENT_PADDING * 2 + targetRowCounter.value * ROW_HEIGHT;
        targetData = {};
    }

    // Use the same height for both panels
    const effectiveSourcePanelHeight = sourcePanelHeight || DEFAULT_PANEL_HEIGHT;
    const effectiveTargetPanelHeight = targetPanelHeight || DEFAULT_PANEL_HEIGHT;
    const maxHeight = Math.max(effectiveSourcePanelHeight, effectiveTargetPanelHeight);

    // Create panel frame nodes with equal heights, then append children
    // Parent nodes must appear before their children in the array
    const allNodes: Node[] = [
        {
            id: "source-panel",
            type: "panelFrame",
            position: { x: 0, y: 0 },
            data: sourceData,
            draggable: false,
            style: { width: panelWidth, height: maxHeight },
        },
        ...sourceNodes,
        {
            id: "target-panel",
            type: "panelFrame",
            position: { x: panelWidth + PANEL_GAP, y: 0 },
            data: targetData,
            draggable: false,
            style: { width: panelWidth, height: maxHeight },
        },
        ...targetNodes,
    ];

    return { nodes: allNodes, maxHeight };
}

// Used for getting the up-to-date display label (#var for variables, #result for everything else) for the source shape in the VDM editor
export function resolveSourceLabel(sourceElementId: string, elements: IElementDictionary, variableSchemas?: Record<string, string>): string {
    if (!sourceElementId) {
        return "";
    }
    const el = elements[sourceElementId];
    if (!el) {
        if (variableSchemas && Object.hasOwn(variableSchemas, sourceElementId)) {
            return `#var.${sourceElementId}`;
        }
        return sourceElementId;
    }

    if (el.type === ElementType.Expression) {
        const hasAssignedVariable = el.parameters?.shouldAssignVariable as { value: boolean | string } | undefined;

        if (hasAssignedVariable && (hasAssignedVariable.value === true || hasAssignedVariable.value === "true")) {
            const varName = el.parameters?.variableName as string | undefined;
            if (varName) {
                return `#var.${varName}`;
            }
        }
    }

    return el.name ? `#result[${el.name}]` : sourceElementId;
}

/**
 * Given a node path (e.g. "user.profile.bio") and a set of collapsed paths,
 * returns the first visible ancestor node ID, or the original path if not collapsed.
 * A node is considered collapsed-hidden if any ancestor path is in the collapsed set.
 */
export function getVisibleAncestor(path: string, side: "source" | "target", collapsedPaths: Set<string>): string {
    let currentPath = path;
    let outermost: string | undefined;

    while (true) {
        if (collapsedPaths.has(currentPath)) {
            outermost = currentPath;
        }

        const cutIndex = currentPath.lastIndexOf(".");

        if (cutIndex === -1) {
            break;
        }

        currentPath = currentPath.slice(0, cutIndex);
    }

    return outermost !== undefined ? `${side}-${outermost}` : `${side}-${path}`;
}

/**
 * Remap edges to account for collapsed nodes.
 * - If a node is inside a collapsed parent, the edge is remapped to the collapsed ancestor.
 * - Duplicate remapped edges are merged (only the first occurrence is kept).
 */
export function remapEdgesForCollapsedNodes(
    edges: Edge[],
    collapsedSourcePaths: Set<string>,
    collapsedTargetPaths: Set<string>,
): Edge[] {
    const seen = new Set<string>();
    const result: Edge[] = [];

    for (const edge of edges) {
        const sourcePath = edge.source.replace(/^source-/, "");
        const targetPath = edge.target.replace(/^target-/, "");

        const mappedSource = getVisibleAncestor(sourcePath, "source", collapsedSourcePaths);
        const mappedTarget = getVisibleAncestor(targetPath, "target", collapsedTargetPaths);

        const edgeKey = `${mappedSource}->${mappedTarget}`;
        if (seen.has(edgeKey)) {
            continue;
        }
        seen.add(edgeKey);

        result.push({
            ...edge,
            id: edgeKey,
            source: mappedSource,
            target: mappedTarget,
        });
    }

    return result;
}

/**
 * Apply collapsed paths to schema properties before flattening.
 * Returns a new schema array with isCollapsed set based on the collapsed paths set.
 */
export function applyCollapsedState(
    schema: JsonSchemaProperty[],
    collapsedPaths: Set<string>,
    parentPath: string = "",
): JsonSchemaProperty[] {
    return schema.map((prop) => {
        const path = buildSchemaPath(parentPath, prop);
        const isExpandable = prop.type === "object" || (prop.type === "array" && !!prop.children?.length);
        const isCollapsed = isExpandable && collapsedPaths.has(path);

        return {
            ...prop,
            isCollapsed,
            children: prop.children && !isCollapsed
                ? applyCollapsedState(prop.children, collapsedPaths, path)
                : prop.children,
        };
    });
}



export function computeMappingWarning(expression: string, targetType: string, sourcePathTypeMap: Record<string, string>, envVarTypeMap: Record<string, string> = {}, mode: VdmExpressionMode = "text"): string | null {
    const sourcePaths = extractSourcePaths(expression, mode);
    const envPaths = extractEnvPaths(expression, mode);

    if ((sourcePaths.length === 0 && envPaths.length === 0) || !targetType) {
        return null;
    }

    const totalRefs = sourcePaths.length + envPaths.length;
    if (totalRefs > 1) {
        return targetType === "string"
            ? "Multiple references are mapped to a single target. Ensure that the expression produces the desired string."
            : `Multiple references are mapped to a single target. Ensure that the expression produces a valid "${targetType}".`;
    }

    // Single source ref
    if (sourcePaths.length === 1) {
        const expectedDirectMapping = formatSourceToken(sourcePaths[0], mode);
        if (expression.trim() !== expectedDirectMapping) {
            return null;
        }
        const sourceType = sourcePathTypeMap[normalizePathForSchema(sourcePaths[0])];
        if (sourceType && sourceType !== targetType) {
            return `Source type "${sourceType}" does not match target type "${targetType}". The value may need conversion.`;
        }
    }

    // Single env ref
    if (envPaths.length === 1) {
        const expectedDirectMapping = mode === "csharp"
            ? `#env.${envPaths[0]}`
            : `{{#env.${envPaths[0]}}}`;
        if (expression.trim() !== expectedDirectMapping) {
            return null;
        }
        const envType = envVarTypeMap[envPaths[0]];
        if (envType && !areTypesCompatible(envType, targetType)) {
            return `Environment variable type "${toJsonSchemaType(envType)}" does not match target type "${targetType}". The value may need conversion.`;
        }
    }

    return null;
}

// Maps backend EnvironmentVariableType (PascalCase) to JSON Schema type (lowercase).
function toJsonSchemaType(envVarType: string): string {
    if (envVarType === "Secret") {
        return "string";
    }
    return envVarType.toLowerCase();
}

function areTypesCompatible(envSchemaType: string, targetType: string): boolean {
    const envJsonType = toJsonSchemaType(envSchemaType);
    if (envJsonType === targetType) {
        return true;
    }
    // Number env vars store integers, compatible with both JSON Schema "number" and "integer"
    if (envJsonType === "number" && targetType === "integer") {
        return true;
    }
    return false;
}

/**
 * Detects mapping entries where the schema types changed since the mapping was saved,
 * causing a type mismatch. For mappings with stored types, only flags when the stored
 * types differ from current (i.e. the schema changed after the connection was drawn).
 * Legacy mappings without stored types flag any mismatch as a fallback.
 */
export function detectTypeMismatchPaths(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Set<string> {
    const mismatched = new Set<string>();
    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        const targetType = findSchemaType(targetSchema, targetPath);
        if (!targetType) {
            continue;
        }
        const sourcePaths = extractSourcePaths(entry.value, entry.mode);
        if (sourcePaths.length !== 1) {
            continue;
        }
        const sourceType = findSchemaType(sourceSchema, sourcePaths[0]);
        if (!sourceType || sourceType === targetType) {
            continue;
        }
        // Current types are mismatched. Check whether the schema changed:
        // - Legacy mappings (no stored types): flag any mismatch as fallback
        // - New mappings (stored types): only flag if stored types differ from current
        if (!entry.sourceType && !entry.targetType) {
            mismatched.add(targetPath);
        } else if (entry.sourceType !== sourceType || entry.targetType !== targetType) {
            mismatched.add(targetPath);
        }
    }
    return mismatched;
}

function normalizeScalarType(type: string): string {
    return type === "integer" ? "number" : type;
}

/** Element type + depth of a primitive array (e.g. "number[]", "number[][]"), or undefined if the array's element type is unknown or structured. */
function primitiveArrayElementDescriptor(prop: JsonSchemaProperty): string | undefined {
    const display = prop.displayType;
    if (display && /^(string|number|integer|boolean)(\[\])+$/.test(display)) {
        const depth = (display.match(/\[\]/g) ?? []).length;
        const base = normalizeScalarType(display.replace(/(\[\])+$/, ""));
        return `${base}${"[]".repeat(depth)}`;
    }
    return undefined;
}

/** Nesting depth of an object-item array. The innermost object level is not encoded in displayType, so depth is bracket count + 1. */
function objectArrayDepth(prop: JsonSchemaProperty): number {
    const brackets = prop.displayType ? (prop.displayType.match(/\[\]/g) ?? []).length : 0;
    return brackets + 1;
}

/**
 * Whether a source value can be assigned directly to a target field without conversion. Recursively
 * compares structure so array element types and nested object-array item shapes are checked, not just
 * the top-level JSON Schema type (both `string[]` and `number[]` are "array"). Permissive when a type
 * is unknown, to avoid dropping mappings whose compatibility cannot be proven.
 */
function arePropsTypeCompatible(source: JsonSchemaProperty, target: JsonSchemaProperty): boolean {
    const sourceType = normalizeScalarType(source.type);
    const targetType = normalizeScalarType(target.type);
    // Untyped schema nodes (no type/properties/items) resolve to "". Treat unknown types as permissive
    // rather than dropping mappings whose compatibility cannot be proven.
    if (!sourceType || !targetType) {
        return true;
    }
    if (sourceType !== targetType) {
        return false;
    }
    if (sourceType === "array") {
        return areArrayItemsCompatible(source, target);
    }
    if (sourceType === "object") {
        return areObjectChildrenCompatible(source.children, target.children);
    }
    return true;
}

function areArrayItemsCompatible(source: JsonSchemaProperty, target: JsonSchemaProperty): boolean {
    const sourceHasObjectItems = !!source.children?.length;
    const targetHasObjectItems = !!target.children?.length;
    if (sourceHasObjectItems || targetHasObjectItems) {
        // Object-item arrays must have matching nesting depth and compatible item object shapes.
        if (!sourceHasObjectItems || !targetHasObjectItems || objectArrayDepth(source) !== objectArrayDepth(target)) {
            return false;
        }
        return areObjectChildrenCompatible(source.children, target.children);
    }
    // Primitive item arrays: compare normalised element descriptors when both are known.
    const sourceItem = primitiveArrayElementDescriptor(source);
    const targetItem = primitiveArrayElementDescriptor(target);
    if (!sourceItem || !targetItem) {
        return true;
    }
    return sourceItem === targetItem;
}

/** Compares overlapping object fields by key; only fields present in both sides must be type-compatible. */
function areObjectChildrenCompatible(
    sourceChildren: JsonSchemaProperty[] | undefined,
    targetChildren: JsonSchemaProperty[] | undefined,
): boolean {
    if (!sourceChildren?.length || !targetChildren?.length) {
        return true;
    }
    const targetByKey = new Map(targetChildren.map((child) => [child.key, child]));
    for (const sourceChild of sourceChildren) {
        const targetChild = targetByKey.get(sourceChild.key);
        if (targetChild && !arePropsTypeCompatible(sourceChild, targetChild)) {
            return false;
        }
    }
    return true;
}

function findSchemaProperty(schema: JsonSchemaProperty[], targetPath: string, parentPath = ""): JsonSchemaProperty | undefined {
    const normalizedTarget = parentPath === "" ? normalizePathForSchema(targetPath) : targetPath;
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        if (path === normalizedTarget) {
            return prop;
        }
        if (prop.children) {
            const child = findSchemaProperty(prop.children, normalizedTarget, path);
            if (child) {
                return child;
            }
        }
    }
    return undefined;
}

/**
 * Reduces a source path as written in an expression to the schema path it addresses, by dropping
 * every indexer segment. Schema paths carry no indexers, so both array indices ("items[0].sku" ->
 * "items.sku") and quoted keys ('dict["foo"].bar' -> "dict.bar") are stripped, including root-indexed
 * paths ("[0].name" -> "name"). Bracket-free paths are returned unchanged, so this is safe (and a
 * no-op) on target paths and already-normalized paths.
 *
 * Call this before comparing or looking up an extracted source path against schema-derived data.
 */
export function normalizePathForSchema(path: string): string {
    return path
        .replace(/\[[^\]]*\]/g, "")
        .replace(/^\.+/, "")
        .replace(/\.{2,}/g, ".");
}

/**
 * Whether the csharp expression is a bare direct reference to a single source field (e.g.
 * "#source.tags" or root-indexed "#source[0].name"), as opposed to a composite or transforming
 * expression. `formatSourceToken` always inserts a leading dot, so root-indexed paths (which start
 * with "[") are matched without it.
 */
function isDirectCsharpSourceMapping(value: string, path: string): boolean {
    const expected = path.startsWith("[") ? `#source${path}` : formatSourceToken(path, "csharp");
    return value.trim() === expected;
}

/**
 * Whether a mapping is a direct, value-preserving assignment of a single source field whose type is
 * incompatible with the target field. Only csharp-mode direct references are checked: text mode
 * stringifies its value, and composite/transforming expressions are intentional and not validated here.
 */
function isTypeIncompatibleDirectMapping(
    entry: VdmMappingEntry,
    targetPath: string,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): boolean {
    if (entry.mode !== "csharp") {
        return false;
    }
    const sourcePaths = extractSourcePaths(entry.value, entry.mode);
    if (sourcePaths.length !== 1 || !isDirectCsharpSourceMapping(entry.value, sourcePaths[0])) {
        return false;
    }
    const sourceProp = findSchemaProperty(sourceSchema, normalizePathForSchema(sourcePaths[0]));
    const targetProp = findSchemaProperty(targetSchema, normalizePathForSchema(targetPath));
    if (!sourceProp || !targetProp) {
        return false;
    }
    return !arePropsTypeCompatible(sourceProp, targetProp);
}

/**
 * Removes auto-generated mappings whose source and target field types are incompatible (e.g. a
 * `string[]` source mapped onto a `number[]` target). Used to enforce that the AI assistant only
 * produces mappings between compatible data types, preventing silent runtime failures under strict
 * schema validation. Returns the surviving mappings and the target paths that were dropped.
 */
export function filterTypeIncompatibleMappings(
    mappings: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): { mappings: Record<string, VdmMappingEntry>; droppedTargetPaths: string[] } {
    const surviving: Record<string, VdmMappingEntry> = {};
    const droppedTargetPaths: string[] = [];
    for (const [targetPath, entry] of Object.entries(mappings)) {
        if (isTypeIncompatibleDirectMapping(entry, targetPath, sourceSchema, targetSchema)) {
            droppedTargetPaths.push(targetPath);
        } else {
            surviving[targetPath] = entry;
        }
    }
    return { mappings: surviving, droppedTargetPaths };
}

/**
 * Acknowledges a type mismatch by re-stamping the entry's stored source/target types to the
 * current schema types, so detectTypeMismatchPaths no longer flags it. Returns the updated
 * mappings, or null when nothing needs to change (no entry, multi-ref mapping, or already in sync).
 */
export function acknowledgeTypeMismatch(
    mappingExpressions: Record<string, VdmMappingEntry>,
    targetPath: string,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): Record<string, VdmMappingEntry> | null {
    const entry = mappingExpressions[targetPath];
    if (!entry) {
        return null;
    }
    const sourcePaths = extractSourcePaths(entry.value, entry.mode);
    if (sourcePaths.length !== 1) {
        return null; // only single-source-ref mappings are ever flagged as type mismatches
    }
    const sourceType = findSchemaType(sourceSchema, sourcePaths[0]) || undefined;
    const targetType = findSchemaType(targetSchema, targetPath) || undefined;
    if (entry.sourceType === sourceType && entry.targetType === targetType) {
        return null;
    }
    return { ...mappingExpressions, [targetPath]: { ...entry, sourceType, targetType } };
}

export interface StaleInfo {
    /** Target paths whose source references were removed. Maps targetPath -> original expression */
    sourceRemovedPaths: Map<string, string>;
    /** Target paths removed from schema. Maps targetPath -> original mapping entry */
    orphanedTargetExpressions: Record<string, VdmMappingEntry>;
    /** Whether any stale mappings were detected */
    hasStale: boolean;
}

/**
 * Detects which mapping entries have become stale after a schema change.
 * Unlike cleanStaleMappings, this does not modify anything - it only reports.
 */
export function detectStaleMappings(
    mappingExpressions: Record<string, VdmMappingEntry>,
    sourceSchema: JsonSchemaProperty[],
    targetSchema: JsonSchemaProperty[],
): StaleInfo {
    const validSourcePaths = collectSchemaPaths(sourceSchema);
    const validTargetPaths = collectSchemaPaths(targetSchema);
    const sourceRemovedPaths = new Map<string, string>();
    const orphanedTargetExpressions: Record<string, VdmMappingEntry> = {};

    for (const [targetPath, entry] of Object.entries(mappingExpressions)) {
        if (!validTargetPaths.has(targetPath)) {
            orphanedTargetExpressions[targetPath] = entry;
            continue;
        }

        const sourcePaths = extractSourcePaths(entry.value, entry.mode);
        if (sourcePaths.length === 0) {
            continue;
        }

        const hasStaleSource = sourcePaths.some(p => p !== "" && !validSourcePaths.has(normalizePathForSchema(p)));
        if (hasStaleSource) {
            sourceRemovedPaths.set(targetPath, entry.value);
        }
    }

    return {
        sourceRemovedPaths,
        orphanedTargetExpressions,
        hasStale: sourceRemovedPaths.size > 0 || Object.keys(orphanedTargetExpressions).length > 0,
    };
}

export function isExpressionAssigningVariable(element: IElement): boolean {
    if (element.type !== ElementType.Expression) {
        return false;
    }

    const shouldAssign = element.parameters?.shouldAssignVariable as { value: boolean | string } | undefined;
    return !!(shouldAssign && (shouldAssign.value === true || shouldAssign.value === "true"));
}

export function getExpressionVariableName(element: IElement): string | undefined {
    return element.parameters?.variableName as string | undefined;
}

export function resolveVdmSourceSchema(
    elements: IElementDictionary,
    variableSchemas: Record<string, string>,
    vdmElement: { parameters?: Record<string, unknown> },
): string | undefined {
    const sourceId = vdmElement.parameters?.sourceNodeId as string | undefined;
    if (!sourceId) {
        return undefined;
    }

    const sourceElement = elements[sourceId];
    if (!sourceElement) {
        return Object.hasOwn(variableSchemas, sourceId) ? variableSchemas[sourceId] : undefined;
    }

    if (isExpressionAssigningVariable(sourceElement)) {
        const varName = getExpressionVariableName(sourceElement);
        if (varName && Object.hasOwn(variableSchemas, varName)) {
            return variableSchemas[varName];
        }
        return undefined;
    }

    return sourceElement.parameters?.schema as string | undefined;
}

function findSchemaTypeRecursive(schema: JsonSchemaProperty[], targetPath: string, parentPath: string): string | undefined {
    for (const prop of schema) {
        const path = buildSchemaPath(parentPath, prop);
        if (path === targetPath) {
            return prop.type;
        }
        if (prop.children) {
            const childType = findSchemaTypeRecursive(prop.children, targetPath, path);
            if (childType) {
                return childType;
            }
        }
    }

    return undefined;
}

/**
 * For a target path being mapped, returns the set of existing mapping paths that would conflict
 * under the "parent and descendant mappings are mutually exclusive" rule.
 *
 * - Descendant mappings under the new path conflict (the new mapping subsumes them).
 * - Ancestor mappings of the new path conflict (the existing mapping subsumes the new one).
 */
export function findConflictingMappings(
    existingMappings: { [path: string]: unknown },
    newPath: string,
    targetSchema?: JsonSchemaProperty[],
): string[] {
    const conflicts: string[] = [];
    const newSegs = resolvePathSegments(newPath, targetSchema);
    for (const path of Object.keys(existingMappings)) {
        if (path !== newPath && segmentsAncestorOrDescendant(resolvePathSegments(path, targetSchema), newSegs)) {
            conflicts.push(path);
        }
    }
    return conflicts;
}

/**
 * Returns target paths whose mapping affordance must be hidden because saving a mapping there
 * would conflict with an existing one (whole-array vs per-element mutex). The conflicting mapping
 * itself is not blocked - the user can still edit or delete it.
 */
export function findBlockedTargetPaths(
    targetSchema: JsonSchemaProperty[] | undefined,
    mappings: { [path: string]: unknown } | undefined,
): Set<string> {
    const blocked = new Set<string>();
    if (!targetSchema || !mappings) {
        return blocked;
    }

    const visit = (schema: JsonSchemaProperty[], parent: string) => {
        for (const prop of schema) {
            const path = buildSchemaPath(parent, prop);
            if (mappings[path] === undefined && findConflictingMappings(mappings, path, targetSchema).length > 0) {
                blocked.add(path);
            }
            if (prop.children) {
                visit(prop.children, path);
            }
        }
    };
    visit(targetSchema, "");
    return blocked;
}

export function buildSchemaPath(parentPath: string, prop: JsonSchemaProperty): string {
    return buildPath(parentPath, prop.pathSegment ?? prop.key);
}

function buildPath(parentPath: string, key: string): string {
    if (!parentPath) {
        return key;
    }

    if (!key) {
        return parentPath;
    }

    return parentPath ? `${parentPath}.${key}` : key;
}

/**
 * Resolves a dotted path to its schema segment chain, matching segments against real keys (which may
 * contain dots) so a literal "user.name" is not read as the nested path user -> name. Mirrors backend
 * VdmEmitBuilder.MatchPath; falls back to a plain split with no schema or a stale/ambiguous path.
 */
function resolvePathSegments(path: string, schema?: JsonSchemaProperty[]): string[] {
    const schemaPath = normalizePathForSchema(path);
    if (schema) {
        const matches: string[][] = [];
        matchPathSegments(schema, schemaPath, [], matches);
        if (matches.length === 1) {
            return matches[0];
        }
    }
    return schemaPath === "" ? [] : schemaPath.split(".");
}

function matchPathSegments(
    nodes: JsonSchemaProperty[],
    remaining: string,
    prefix: string[],
    matches: string[][],
): void {
    for (const node of nodes) {
        const seg = node.pathSegment ?? node.key;
        if (seg === "") {
            // Transparent root-array wrapper: the empty path is its whole-array mapping; its children
            // sit at the same level (buildPath collapses the empty segment).
            if (remaining === "") {
                matches.push([...prefix]);
            }
            if (node.children) {
                matchPathSegments(node.children, remaining, prefix, matches);
            }
            continue;
        }
        if (remaining === seg) {
            matches.push([...prefix, seg]);
        } else if (node.children
            && remaining.length > seg.length
            && remaining.startsWith(seg)
            && remaining[seg.length] === ".") {
            matchPathSegments(node.children, remaining.slice(seg.length + 1), [...prefix, seg], matches);
        }
    }
}

// Mutex ancestry: one segment chain is a prefix of the other (equal included; [] is a prefix of all).
function segmentsAncestorOrDescendant(a: string[], b: string[]): boolean {
    return isSegmentPrefix(a, b) || isSegmentPrefix(b, a);
}

function isSegmentPrefix(prefix: string[], full: string[]): boolean {
    return prefix.length <= full.length && prefix.every((seg, i) => seg === full[i]);
}